import { useState, useEffect } from "react";

// scheduled_at is timestamptz; values are wall-clock times stored as UTC.
// Formatting with timeZone:'UTC' reproduces the entered clock time verbatim;
// local-timezone formatting would shift it.
// SCHED-5 calendar MUST use the UTC date components for day bucketing —
// local-timezone getters would bucket evening calls onto the wrong day.
export function parseScheduledAt(s) {
  if (!s) return null;
  try {
    const d = new Date(s);
    if (isNaN(d.getTime())) return null;
    return {
      formatted: d.toLocaleString(undefined, {
        timeZone: 'UTC',
        weekday: 'short', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit',
      }),
      timeFormatted: d.toLocaleString(undefined, {
        timeZone: 'UTC',
        hour: 'numeric', minute: '2-digit',
      }),
      year: d.getUTCFullYear(),
      month: d.getUTCMonth(),
      day: d.getUTCDate(),
      hours: d.getUTCHours(),
      minutes: d.getUTCMinutes(),
    };
  } catch { return null; }
}

const CAL_MONTH_NAMES = ['January','February','March','April','May','June',
  'July','August','September','October','November','December'];
const CAL_DAY_HEADERS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

export default function SchedulingView({ API }) {
  const [entries, setEntries] = useState([]);
  const [collapsed, setCollapsed] = useState({ qualifying: false, scheduling: false, pending: false, calendar: false });
  const [timesInputs, setTimesInputs] = useState({});
  const [contextInputs, setContextInputs] = useState({});

  const [statementExpanded, setStatementExpanded] = useState(new Set());
  const [statementDraft, setStatementDraft] = useState({});

  const [addExpertOpen, setAddExpertOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [addProjectId, setAddProjectId] = useState('');
  const [addProjectExperts, setAddProjectExperts] = useState([]);
  const [addExpertId, setAddExpertId] = useState('');
  const [addLoading, setAddLoading] = useState(false);

  const [toast, setToast] = useState(null);

  // SCHED-4: confirm datetime expansion
  const [confirmExpanded, setConfirmExpanded] = useState(new Set());
  const [confirmDate, setConfirmDate] = useState({});
  const [confirmTime, setConfirmTime] = useState({});

  // SCHED-5: calendar
  const [calYear, setCalYear] = useState(() => new Date().getUTCFullYear());
  const [calMonth, setCalMonth] = useState(() => new Date().getUTCMonth());
  const [selectedChipId, setSelectedChipId] = useState(null);
  const [reschedDate, setReschedDate] = useState('');
  const [reschedTime, setReschedTime] = useState('');

  // UI-1: per-row archive statement expand
  const [archiveOpen, setArchiveOpen] = useState(new Set());

  const [archiveSectionOpen, setArchiveSectionOpen] = useState(() => {
    try { return localStorage.getItem('rasp.archiveCollapsed') !== '1'; } catch { return true; }
  });

  useEffect(() => {
    const token = localStorage.getItem('expertnet_token');
    const headers = { Authorization: `Bearer ${token}` };

    const fetchEntries = async () => {
      try {
        const res = await fetch(`${API}/scheduling`, { headers });
        if (res.ok) {
          const data = await res.json();
          setEntries(data.entries);
          const timesMap = {};
          const contextMap = {};
          data.entries.forEach(e => {
            timesMap[e.id] = e.times || '';
            contextMap[e.id] = e.qualifying_context || '';
          });
          setTimesInputs(timesMap);
          setContextInputs(contextMap);
        }
      } catch (e) { console.error(e); }
    };

    const fetchProjects = async () => {
      try {
        const res = await fetch(`${API}/projects`, { headers });
        if (res.ok) setProjects(await res.json());
      } catch (e) { console.error(e); }
    };

    fetchEntries();
    fetchProjects();
  }, [API]);

  const patchEntry = async (id, body) => {
    try {
      const token = localStorage.getItem('expertnet_token');
      const res = await fetch(`${API}/scheduling/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const updated = await res.json();
        setEntries(prev => prev.map(e => e.id === id ? updated : e));
        return true;
      }
    } catch (e) { console.error(e); }
    return false;
  };

  const deleteEntry = async (id) => {
    try {
      const token = localStorage.getItem('expertnet_token');
      const res = await fetch(`${API}/scheduling/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setEntries(prev => prev.filter(e => e.id !== id));
      }
    } catch (e) { console.error(e); }
  };

  const handleProjectSelect = async (projectId) => {
    setAddProjectId(projectId);
    setAddExpertId('');
    setAddProjectExperts([]);
    if (!projectId) return;
    try {
      const token = localStorage.getItem('expertnet_token');
      const res = await fetch(`${API}/projects/${projectId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setAddProjectExperts(data.experts || []);
      }
    } catch (e) { console.error(e); }
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const handleAddExpert = async () => {
    if (!addProjectId || !addExpertId || addLoading) return;
    setAddLoading(true);
    try {
      const token = localStorage.getItem('expertnet_token');
      const res = await fetch(`${API}/scheduling`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ project_id: parseInt(addProjectId), expert_id: parseInt(addExpertId), kind: 'qualifying' }),
      });
      const data = await res.json();
      if (res.ok) {
        if (data.added) {
          setEntries(prev => [...prev, data.entry]);
          setContextInputs(prev => ({ ...prev, [data.entry.id]: data.entry.qualifying_context || '' }));
          setAddExpertOpen(false);
          setAddProjectId('');
          setAddExpertId('');
          setAddProjectExperts([]);
        } else {
          const pid = parseInt(addProjectId);
          const eid = parseInt(addExpertId);
          const existing = entries.find(e => e.project_id === pid && e.expert_id === eid);
          if (existing && existing.statement_received_at) {
            showToast('In archive — use Restore');
          } else {
            showToast('Already in Qualifying.');
          }
        }
      }
    } catch (e) { console.error(e); }
    finally { setAddLoading(false); }
  };

  // ── Derived ──────────────────────────────────────────────────────────────────

  const qualifyingEntries = entries.filter(e => e.kind === 'qualifying');
  const queueRows = qualifyingEntries.filter(e => !e.statement_received_at);
  const archiveRows = qualifyingEntries.filter(e => e.statement_received_at);

  const scheduleAll = entries.filter(e => e.kind === 'schedule');
  const schedulingRows = scheduleAll.filter(e => !e.scheduled_at && !(e.compliance === 1 && (e.times || '').trim()));
  const pendingRows    = scheduleAll.filter(e => !e.scheduled_at && e.compliance === 1 && (e.times || '').trim());
  const confirmedRows  = scheduleAll.filter(e => e.scheduled_at);

  const toggleSection = (key) => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

  const fmtDate = (s) => {
    if (!s) return '—';
    try {
      return new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return s; }
  };

  // ── Calendar derivation (sole parse site: all uses route through parsedSchedules) ──
  const parsedSchedules = {};
  for (const entry of confirmedRows) {
    const p = parseScheduledAt(entry.scheduled_at);
    if (p) parsedSchedules[entry.id] = p;
  }

  const chipsByDay = {};
  for (const entry of confirmedRows) {
    const p = parsedSchedules[entry.id];
    if (!p || p.year !== calYear || p.month !== calMonth) continue;
    if (!chipsByDay[p.day]) chipsByDay[p.day] = [];
    chipsByDay[p.day].push(entry);
  }
  for (const day of Object.keys(chipsByDay)) {
    chipsByDay[day].sort((a, b) => {
      const pa = parsedSchedules[a.id], pb = parsedSchedules[b.id];
      if (pa.hours !== pb.hours) return pa.hours - pb.hours;
      return pa.minutes - pb.minutes;
    });
  }

  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  const firstDayOfWeek = new Date(calYear, calMonth, 1).getDay();
  const calCells = [];
  for (let i = 0; i < firstDayOfWeek; i++) calCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) calCells.push(d);
  while (calCells.length % 7 !== 0) calCells.push(null);

  const todayDate = new Date();
  const todayY = todayDate.getUTCFullYear();
  const todayM = todayDate.getUTCMonth();
  const todayD = todayDate.getUTCDate();

  const selectedEntry = selectedChipId != null
    ? confirmedRows.find(e => e.id === selectedChipId) ?? null
    : null;
  const selectedParsed = selectedEntry ? parsedSchedules[selectedEntry.id] ?? null : null;

  const handleChipClick = (entry) => {
    if (selectedChipId === entry.id) {
      setSelectedChipId(null); setReschedDate(''); setReschedTime('');
    } else {
      const p = parsedSchedules[entry.id];
      setSelectedChipId(entry.id);
      if (p) {
        setReschedDate(`${p.year}-${String(p.month + 1).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`);
        setReschedTime(`${String(p.hours).padStart(2, '0')}:${String(p.minutes).padStart(2, '0')}`);
      }
    }
  };

  const prevMonth = () => {
    if (calMonth === 0) { setCalYear(y => y - 1); setCalMonth(11); }
    else { setCalMonth(m => m - 1); }
  };
  const nextMonth = () => {
    if (calMonth === 11) { setCalYear(y => y + 1); setCalMonth(0); }
    else { setCalMonth(m => m + 1); }
  };

  // ── Styles ───────────────────────────────────────────────────────────────────

  const hCell = {
    fontFamily: 'var(--font-data)',
    fontSize: 10,
    fontWeight: 600,
    color: 'var(--text-4)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    padding: '0 12px',
    display: 'flex',
    alignItems: 'center',
  };

  const inputStyle = {
    background: 'var(--input-bg)',
    border: '1px solid var(--line)',
    color: 'var(--text-1)',
    fontSize: 12,
    height: 30,
    padding: '0 10px',
    borderRadius: 8,
    width: '100%',
    outline: 'none',
    boxSizing: 'border-box',
    colorScheme: 'dark',
  };

  const selectStyle = {
    background: 'var(--input-bg)',
    border: '1px solid var(--line)',
    color: 'var(--text-1)',
    fontSize: 12,
    height: 30,
    padding: '0 10px',
    borderRadius: 8,
    outline: 'none',
    colorScheme: 'dark',
  };

  // 24×24 remove button; hover handled by .sched-xbtn CSS class
  const xBtn = {
    background: 'none',
    border: 'none',
    color: 'var(--text-4)',
    cursor: 'pointer',
    width: 24,
    height: 24,
    borderRadius: 5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    fontSize: 12,
    flexShrink: 0,
  };

  const emptyState = {
    textAlign: 'center',
    padding: '40px 0',
    color: 'var(--text-3)',
    fontSize: 13,
  };

  // Must have style attribute — sched2 test (d) uses closest('div[style]')
  const sectionHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 44,
    padding: '0 18px',
    cursor: 'pointer',
    userSelect: 'none',
  };

  // CSS chevron: 6px square, 1.5px right+bottom border, rotated
  const chevronEl = (key) => (
    <span style={{
      display: 'inline-block',
      width: 6,
      height: 6,
      borderRight: '1.5px solid var(--text-3)',
      borderBottom: '1.5px solid var(--text-3)',
      transform: collapsed[key] ? 'rotate(-45deg)' : 'rotate(45deg)',
      transition: 'transform 0.2s',
      flexShrink: 0,
    }} />
  );

  // 8px dot with glow
  const dot = (color) => (
    <span style={{
      display: 'inline-block',
      width: 8,
      height: 8,
      borderRadius: '50%',
      background: color,
      boxShadow: `0 0 8px ${color}`,
      flexShrink: 0,
    }} />
  );

  // ── Column templates ──────────────────────────────────────────────────────────
  const schedCols = '2fr 90px 2fr 1.5fr 1.5fr 40px';
  const pendCols  = '2fr 90px 2fr 1.5fr 1.5fr 100px 40px';
  const qualCols  = '180px 1fr 180px 140px 90px 32px';
  const archCols  = '180px 1fr 120px 180px 120px 80px';

  return (
    <>
      <style>{`
        .sched-row:hover { background: var(--row-hover) !important; }
        .sched-xbtn:hover { background: rgba(224,106,90,.12) !important; color: var(--negative) !important; }
      `}</style>

      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: 'var(--glass-card)', color: 'var(--text-1)', padding: '8px 16px', borderRadius: 8,
          fontSize: 13, zIndex: 1000, boxShadow: 'var(--card-shadow)', border: '1px solid var(--line-soft)',
        }}>{toast}</div>
      )}

      <div style={{ padding: '24px', maxWidth: '1440px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 14 }}>

        {/* ── Page header ── */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <h1 style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.01em', color: 'var(--text-1)', margin: 0 }}>Scheduling</h1>
          <div style={{ display: 'flex', gap: 24 }}>
            {[
              { label: 'QUALIFYING', value: queueRows.length, color: 'var(--warning)' },
              { label: 'SCHEDULING', value: schedulingRows.length, color: 'var(--accent)' },
              { label: 'PENDING', value: pendingRows.length, color: 'var(--text-3)' },
              { label: 'THIS MONTH', value: confirmedRows.filter(e => { const p = parsedSchedules[e.id]; return p && p.year === todayY && p.month === todayM; }).length, color: 'var(--text-1)' },
            ].map(({ label, value, color }) => (
              <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span className="r-eyebrow">{label}</span>
                <span style={{ fontFamily: 'var(--font-data)', fontSize: 20, fontWeight: 500, color, lineHeight: 1 }}>{value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Qualifying ── */}
        <div className="r-card">
          <div style={sectionHeaderStyle} onClick={() => toggleSection('qualifying')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {dot('var(--warning)')}
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)' }}>Qualifying</span>
              <span style={{ fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--text-4)' }}>{queueRows.length}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button
                className="r-btn r-btn-ghost"
                style={{ height: 28, fontSize: 12 }}
                onClick={(e) => { e.stopPropagation(); setAddExpertOpen(prev => !prev); }}
              >+ Add expert</button>
              {chevronEl('qualifying')}
            </div>
          </div>

          <div style={{ display: collapsed.qualifying ? 'none' : 'block', borderTop: '1px solid var(--line)' }}>

            {addExpertOpen && (
              <div style={{ padding: '10px 18px', borderBottom: '1px solid var(--line)', background: 'var(--subbar-bg)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <select
                  style={{ ...selectStyle, flex: '1 1 180px' }}
                  value={addProjectId}
                  onChange={(e) => handleProjectSelect(e.target.value)}
                >
                  <option value="">Select project…</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <select
                  style={{ ...selectStyle, flex: '1 1 180px' }}
                  value={addExpertId}
                  onChange={(e) => setAddExpertId(e.target.value)}
                  disabled={!addProjectId}
                >
                  <option value="">Select expert…</option>
                  {addProjectExperts.map(ex => <option key={ex.id} value={ex.id}>{ex.name}</option>)}
                </select>
                <button
                  className="r-btn r-btn-primary"
                  style={{ height: 28, ...(!addProjectId || !addExpertId || addLoading ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
                  disabled={!addProjectId || !addExpertId || addLoading}
                  onClick={handleAddExpert}
                >{addLoading ? '…' : 'Confirm'}</button>
                <button
                  className="r-btn r-btn-ghost"
                  style={{ height: 28, fontSize: 12 }}
                  onClick={() => { setAddExpertOpen(false); setAddProjectId(''); setAddExpertId(''); setAddProjectExperts([]); }}
                >Cancel</button>
              </div>
            )}

            {queueRows.length === 0
              ? <div style={emptyState}>No qualifying statements needed right now.</div>
              : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: qualCols, height: 32, background: 'var(--subbar-bg)', alignItems: 'center', borderBottom: '1px solid var(--line)' }}>
                    <span style={hCell}>Expert</span>
                    <span style={hCell}>Qualifying context</span>
                    <span style={hCell}>Project</span>
                    <span style={hCell}>Client</span>
                    <span style={{ ...hCell, justifyContent: 'center' }}>Stmt rcvd</span>
                    <span />
                  </div>
                  {queueRows.map((entry, idx) => {
                    const isExpanded = statementExpanded.has(entry.id);
                    const draft = statementDraft[entry.id] ?? '';
                    return (
                      <div key={entry.id}>
                        <div
                          className="sched-row"
                          style={{ display: 'grid', gridTemplateColumns: qualCols, background: idx % 2 === 0 ? 'var(--row-odd)' : 'transparent', borderBottom: isExpanded ? 'none' : '1px solid var(--line-soft)', alignItems: 'center', minHeight: 44 }}
                        >
                          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-1)', padding: '0 12px' }}>{entry.expert_name}</span>
                          <div style={{ padding: '0 12px' }}>
                            <input
                              type="text"
                              style={inputStyle}
                              value={contextInputs[entry.id] ?? ''}
                              onChange={(e) => setContextInputs(prev => ({ ...prev, [entry.id]: e.target.value }))}
                              onBlur={async (e) => { await patchEntry(entry.id, { qualifying_context: e.target.value }); }}
                              placeholder="Add qualifying context..."
                            />
                          </div>
                          <span style={{ fontSize: 12, color: 'var(--text-2)', padding: '0 12px' }}>{entry.project_name}</span>
                          <span style={{ fontSize: 12, color: 'var(--text-3)', padding: '0 12px' }}>{entry.client || '—'}</span>
                          <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <input
                              type="checkbox"
                              checked={isExpanded}
                              onChange={(ev) => {
                                if (ev.target.checked) {
                                  setStatementExpanded(prev => new Set(prev).add(entry.id));
                                  setStatementDraft(prev => ({ ...prev, [entry.id]: entry.statement || '' }));
                                } else {
                                  setStatementExpanded(prev => { const s = new Set(prev); s.delete(entry.id); return s; });
                                }
                              }}
                              style={{ width: 16, height: 16, cursor: 'pointer', accentColor: 'var(--accent)' }}
                            />
                          </div>
                          <button
                            className="sched-xbtn"
                            style={xBtn}
                            onClick={async () => {
                              if (window.confirm('Remove this expert from scheduling?')) {
                                await deleteEntry(entry.id);
                              }
                            }}
                            title="Remove"
                          >✕</button>
                        </div>
                        {isExpanded && (
                          <div style={{ padding: '10px 18px', borderBottom: '1px solid var(--line-soft)', background: 'var(--subbar-bg)' }}>
                            <textarea
                              style={{ ...inputStyle, height: 'auto', minHeight: 72, resize: 'vertical', display: 'block', padding: '8px 10px' }}
                              value={draft}
                              onChange={(e) => setStatementDraft(prev => ({ ...prev, [entry.id]: e.target.value }))}
                              placeholder="Enter qualifying statement text..."
                            />
                            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                              <button
                                className="r-btn r-btn-primary"
                                style={{ height: 28, ...(!draft.trim() ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
                                disabled={!draft.trim()}
                                onClick={async () => {
                                  const ok = await patchEntry(entry.id, { statement: draft.trim(), statement_received_at: true });
                                  if (ok) {
                                    setStatementExpanded(prev => { const s = new Set(prev); s.delete(entry.id); return s; });
                                    setStatementDraft(prev => { const d = { ...prev }; delete d[entry.id]; return d; });
                                  }
                                }}
                              >Save</button>
                              <button
                                className="r-btn r-btn-ghost"
                                style={{ height: 28, fontSize: 12 }}
                                onClick={() => {
                                  setStatementExpanded(prev => { const s = new Set(prev); s.delete(entry.id); return s; });
                                  setStatementDraft(prev => { const d = { ...prev }; delete d[entry.id]; return d; });
                                }}
                              >Cancel</button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              )
            }

            {/* Statement Archive */}
            <div style={{ borderTop: '1px solid var(--line)' }}>
              <div
                onClick={() => {
                  const next = !archiveSectionOpen;
                  setArchiveSectionOpen(next);
                  try { localStorage.setItem('rasp.archiveCollapsed', next ? '0' : '1'); } catch {}
                }}
                style={{ height: 36, background: 'var(--subbar-bg)', display: 'flex', alignItems: 'center', gap: 6, padding: '0 18px', borderBottom: archiveSectionOpen ? '1px solid var(--line)' : 'none', cursor: 'pointer', userSelect: 'none' }}
              >
                <span className="r-eyebrow">Statement Archive</span>
                <span style={{ fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--text-4)' }}>{archiveRows.length}</span>
                <span style={{
                  display: 'inline-block', width: 6, height: 6,
                  borderRight: '1.5px solid var(--text-3)', borderBottom: '1.5px solid var(--text-3)',
                  transform: archiveSectionOpen ? 'rotate(45deg)' : 'rotate(-45deg)',
                  transition: 'transform 0.2s', flexShrink: 0, marginLeft: 2,
                }} />
              </div>
              {archiveSectionOpen && (archiveRows.length === 0
                ? <div style={{ ...emptyState, padding: '24px 0' }}>No statements received yet.</div>
                : (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: archCols, height: 32, background: 'var(--subbar-bg)', alignItems: 'center', borderBottom: '1px solid var(--line)' }}>
                      <span style={hCell}>Expert</span>
                      <span style={hCell}>Statement</span>
                      <span style={hCell}>Received</span>
                      <span style={hCell}>Project</span>
                      <span style={hCell}>Client</span>
                      <span />
                    </div>
                    {archiveRows.map((entry, idx) => {
                      const isOpen = archiveOpen.has(entry.id);
                      const stmt = entry.statement || '';
                      const isLong = stmt.length > 160;
                      return (
                        <div
                          key={entry.id}
                          className="sched-row"
                          style={{ display: 'grid', gridTemplateColumns: archCols, background: idx % 2 === 0 ? 'var(--row-odd)' : 'transparent', borderBottom: '1px solid var(--line-soft)', alignItems: 'start', padding: '8px 0' }}
                        >
                          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-1)', padding: '0 12px' }}>{entry.expert_name}</span>
                          <div style={{ padding: '0 12px' }}>
                            <span style={{ fontSize: 12, color: 'var(--text-2)', wordBreak: 'break-word' }}>
                              {isLong && !isOpen ? stmt.slice(0, 160) + '…' : stmt}
                            </span>
                            {isLong && (
                              <button
                                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, color: 'var(--accent)', display: 'block', marginTop: 2 }}
                                onClick={() => setArchiveOpen(prev => {
                                  const s = new Set(prev);
                                  if (s.has(entry.id)) s.delete(entry.id); else s.add(entry.id);
                                  return s;
                                })}
                              >{isOpen ? 'Show less' : 'Read full statement'}</button>
                            )}
                          </div>
                          <span style={{ fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--text-3)', padding: '0 12px' }}>{fmtDate(entry.statement_received_at)}</span>
                          <span style={{ fontSize: 12, color: 'var(--text-2)', padding: '0 12px' }}>{entry.project_name}</span>
                          <span style={{ fontSize: 12, color: 'var(--text-3)', padding: '0 12px' }}>{entry.client || '—'}</span>
                          <div style={{ padding: '2px 12px' }}>
                            <button
                              className="r-btn r-btn-tint"
                              style={{ height: 26 }}
                              onClick={async () => { await patchEntry(entry.id, { statement_received_at: null }); }}
                            >Restore</button>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )
              )}
            </div>

          </div>
        </div>

        {/* ── Scheduling ── */}
        <div className="r-card">
          <div style={sectionHeaderStyle} onClick={() => toggleSection('scheduling')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {dot('var(--accent)')}
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)' }}>Scheduling</span>
              <span style={{ fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--text-4)' }}>{schedulingRows.length}</span>
            </div>
            {chevronEl('scheduling')}
          </div>
          <div style={{ display: collapsed.scheduling ? 'none' : 'block', borderTop: '1px solid var(--line)' }}>
            {schedulingRows.length === 0
              ? <div style={emptyState}>No experts in Scheduling yet.</div>
              : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: schedCols, height: 32, background: 'var(--subbar-bg)', alignItems: 'center', borderBottom: '1px solid var(--line)' }}>
                    <span style={hCell}>Expert</span>
                    <span style={{ ...hCell, justifyContent: 'center' }}>Compliance</span>
                    <span style={hCell}>Availability</span>
                    <span style={hCell}>Project</span>
                    <span style={hCell}>Client</span>
                    <span />
                  </div>
                  {schedulingRows.map((entry, idx) => (
                    <div
                      key={entry.id}
                      className="sched-row"
                      style={{ display: 'grid', gridTemplateColumns: schedCols, background: idx % 2 === 0 ? 'var(--row-odd)' : 'transparent', borderBottom: '1px solid var(--line-soft)', alignItems: 'center', minHeight: 44 }}
                    >
                      <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-1)', padding: '0 12px' }}>{entry.expert_name}</span>
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <input
                          type="checkbox"
                          checked={entry.compliance === 1}
                          onChange={async (e) => { await patchEntry(entry.id, { compliance: e.target.checked ? 1 : 0 }); }}
                          style={{ width: 16, height: 16, cursor: 'pointer', accentColor: 'var(--accent)' }}
                        />
                      </div>
                      <div style={{ padding: '0 12px' }}>
                        <input
                          type="text"
                          style={inputStyle}
                          value={timesInputs[entry.id] ?? ''}
                          onChange={(e) => setTimesInputs(prev => ({ ...prev, [entry.id]: e.target.value }))}
                          onBlur={async (e) => { await patchEntry(entry.id, { times: e.target.value }); }}
                          placeholder="e.g. Mon/Wed 2–4pm"
                        />
                      </div>
                      <span style={{ fontSize: 12, color: 'var(--text-2)', padding: '0 12px' }}>{entry.project_name}</span>
                      <span style={{ fontSize: 12, color: 'var(--text-3)', padding: '0 12px' }}>{entry.client || '—'}</span>
                      <button
                        className="sched-xbtn"
                        style={xBtn}
                        onClick={() => deleteEntry(entry.id)}
                        title="Remove"
                      >✕</button>
                    </div>
                  ))}
                </>
              )
            }
          </div>
        </div>

        {/* ── Pending ── */}
        <div className="r-card">
          <div style={sectionHeaderStyle} onClick={() => toggleSection('pending')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {dot('var(--st-pending)')}
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)' }}>Pending</span>
              <span style={{ fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--text-4)' }}>{pendingRows.length}</span>
            </div>
            {chevronEl('pending')}
          </div>
          <div style={{ display: collapsed.pending ? 'none' : 'block', borderTop: '1px solid var(--line)' }}>
            {pendingRows.length === 0
              ? <div style={emptyState}>No experts pending confirmation.</div>
              : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: pendCols, height: 32, background: 'var(--subbar-bg)', alignItems: 'center', borderBottom: '1px solid var(--line)' }}>
                    <span style={hCell}>Expert</span>
                    <span style={{ ...hCell, justifyContent: 'center' }}>Compliance</span>
                    <span style={hCell}>Availability</span>
                    <span style={hCell}>Project</span>
                    <span style={hCell}>Client</span>
                    <span />
                    <span />
                  </div>
                  {pendingRows.map((entry, idx) => {
                    const isConfirmOpen = confirmExpanded.has(entry.id);
                    const d = confirmDate[entry.id] || '';
                    const t = confirmTime[entry.id] || '';
                    return (
                      <div key={entry.id}>
                        <div
                          className="sched-row"
                          style={{ display: 'grid', gridTemplateColumns: pendCols, background: idx % 2 === 0 ? 'var(--row-odd)' : 'transparent', borderBottom: isConfirmOpen ? 'none' : '1px solid var(--line-soft)', alignItems: 'center', minHeight: 44 }}
                        >
                          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-1)', padding: '0 12px' }}>{entry.expert_name}</span>
                          <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <input
                              type="checkbox"
                              checked={entry.compliance === 1}
                              onChange={async (ev) => {
                                if (!ev.target.checked) {
                                  if (!window.confirm(`Move ${entry.expert_name} back to Scheduling?`)) return;
                                }
                                await patchEntry(entry.id, { compliance: ev.target.checked ? 1 : 0 });
                              }}
                              style={{ width: 16, height: 16, cursor: 'pointer', accentColor: 'var(--accent)' }}
                            />
                          </div>
                          <div style={{ padding: '0 12px' }}>
                            <input
                              type="text"
                              style={inputStyle}
                              value={timesInputs[entry.id] ?? ''}
                              onChange={(e) => setTimesInputs(prev => ({ ...prev, [entry.id]: e.target.value }))}
                              onBlur={async (ev) => {
                                const newVal = ev.target.value;
                                const prevVal = entry.times || '';
                                if (!newVal.trim() && prevVal.trim()) {
                                  if (!window.confirm(`Move ${entry.expert_name} back to Scheduling?`)) {
                                    setTimesInputs(prev => ({ ...prev, [entry.id]: prevVal }));
                                    return;
                                  }
                                }
                                await patchEntry(entry.id, { times: newVal });
                              }}
                              placeholder="e.g. Mon/Wed 2–4pm"
                            />
                          </div>
                          <span style={{ fontSize: 12, color: 'var(--text-2)', padding: '0 12px' }}>{entry.project_name}</span>
                          <span style={{ fontSize: 12, color: 'var(--text-3)', padding: '0 12px' }}>{entry.client || '—'}</span>
                          <button
                            className={isConfirmOpen ? 'r-btn r-btn-primary' : 'r-btn r-btn-tint'}
                            style={{ height: 28 }}
                            onClick={() => {
                              setConfirmExpanded(prev => {
                                const s = new Set(prev);
                                if (s.has(entry.id)) { s.delete(entry.id); } else { s.add(entry.id); }
                                return s;
                              });
                              if (!confirmExpanded.has(entry.id)) {
                                setConfirmDate(prev => ({ ...prev, [entry.id]: '' }));
                                setConfirmTime(prev => ({ ...prev, [entry.id]: '' }));
                              }
                            }}
                          >Confirm</button>
                          <button
                            className="sched-xbtn"
                            style={xBtn}
                            onClick={() => deleteEntry(entry.id)}
                            title="Remove"
                          >✕</button>
                        </div>
                        {isConfirmOpen && (
                          <div style={{ padding: '10px 18px', borderBottom: '1px solid var(--line-soft)', background: 'var(--subbar-bg)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            <input
                              type="date"
                              style={{ ...inputStyle, width: 'auto', flex: '0 0 auto', fontFamily: 'var(--font-data)' }}
                              value={d}
                              onChange={(e) => setConfirmDate(prev => ({ ...prev, [entry.id]: e.target.value }))}
                            />
                            <input
                              type="time"
                              style={{ ...inputStyle, width: 'auto', flex: '0 0 auto', fontFamily: 'var(--font-data)' }}
                              value={t}
                              onChange={(e) => setConfirmTime(prev => ({ ...prev, [entry.id]: e.target.value }))}
                            />
                            <button
                              className="r-btn r-btn-primary"
                              style={{ height: 28, ...(!d || !t ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
                              disabled={!d || !t}
                              onClick={async () => {
                                const ok = await patchEntry(entry.id, { scheduled_at: `${d}T${t}` });
                                if (ok) {
                                  setConfirmExpanded(prev => { const s = new Set(prev); s.delete(entry.id); return s; });
                                }
                              }}
                            >Save</button>
                            <button
                              className="r-btn r-btn-ghost"
                              style={{ height: 28, fontSize: 12 }}
                              onClick={() => {
                                setConfirmExpanded(prev => { const s = new Set(prev); s.delete(entry.id); return s; });
                              }}
                            >Cancel</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              )
            }
          </div>
        </div>

        {/* ── Calendar ── */}
        <div className="r-card">
          <div style={sectionHeaderStyle} onClick={() => toggleSection('calendar')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)' }}>Calendar</span>
              <span style={{ fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--text-4)' }}>{confirmedRows.length}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                className="r-btn r-btn-ghost"
                style={{ width: 28, height: 28, padding: 0 }}
                onClick={(e) => { e.stopPropagation(); prevMonth(); }}
                aria-label="Previous month"
              >‹</button>
              <span style={{ fontFamily: 'var(--font-data)', fontSize: 13, fontWeight: 500, color: 'var(--text-1)', minWidth: 120, textAlign: 'center' }}>
                {CAL_MONTH_NAMES[calMonth]} {calYear}
              </span>
              <button
                className="r-btn r-btn-ghost"
                style={{ width: 28, height: 28, padding: 0 }}
                onClick={(e) => { e.stopPropagation(); nextMonth(); }}
                aria-label="Next month"
              >›</button>
              {chevronEl('calendar')}
            </div>
          </div>

          <div style={{ display: collapsed.calendar ? 'none' : 'block', borderTop: '1px solid var(--line)' }}>

            {/* DOW row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', height: 28, alignItems: 'center' }}>
              {CAL_DAY_HEADERS.map(h => (
                <div key={h} style={{ textAlign: 'center', fontFamily: 'var(--font-data)', fontSize: 10, fontWeight: 600, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{h}</div>
              ))}
            </div>

            {/* Calendar grid — background fills 1px gaps between cells */}
            <div style={{ background: 'var(--line-soft)', display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
              {calCells.map((day, i) => {
                const isToday = day !== null && calYear === todayY && calMonth === todayM && day === todayD;
                const isPast = day !== null && (calYear < todayY || (calYear === todayY && (calMonth < todayM || (calMonth === todayM && day < todayD))));
                const chips = day !== null ? (chipsByDay[day] || []) : [];
                return (
                  <div
                    key={i}
                    style={{
                      minHeight: 86,
                      background: isToday ? 'rgba(45,212,191,0.06)' : 'var(--glass-card)',
                      padding: 6,
                      outline: isToday ? '1px solid rgba(45,212,191,.5)' : 'none',
                    }}
                  >
                    {day !== null && (
                      <>
                        <div style={{
                          fontFamily: 'var(--font-data)',
                          fontSize: 11,
                          color: isToday ? 'var(--accent)' : isPast ? 'var(--text-4)' : 'var(--text-3)',
                          fontWeight: isToday ? 600 : 400,
                          textAlign: 'right',
                          marginBottom: 3,
                        }}>{day}</div>
                        {chips.map(entry => {
                          const p = parsedSchedules[entry.id];
                          const isSelected = selectedChipId === entry.id;
                          return (
                            <button
                              key={entry.id}
                              onClick={() => handleChipClick(entry)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 3,
                                width: '100%',
                                height: 20,
                                background: isSelected ? 'rgba(45,212,191,.22)' : 'rgba(45,212,191,.08)',
                                border: isSelected ? '1px solid var(--accent)' : '1px solid rgba(45,212,191,.25)',
                                borderRadius: 4,
                                padding: '0 4px',
                                cursor: 'pointer',
                                marginBottom: 2,
                                overflow: 'hidden',
                                boxSizing: 'border-box',
                              }}
                            >
                              <span style={{ fontSize: 11, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 1, minWidth: 0 }}>{entry.expert_name}</span>
                              {p?.timeFormatted && <span style={{ fontFamily: 'var(--font-data)', fontSize: 10, color: 'var(--accent)', flexShrink: 0 }}>{p.timeFormatted}</span>}
                            </button>
                          );
                        })}
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Detail panel */}
            {selectedEntry && (
              <div style={{ margin: '0 18px 18px', padding: 16, background: 'var(--glass-card)', border: '1px solid var(--line-soft)', borderRadius: 10, boxShadow: 'var(--card-shadow)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', marginBottom: 4 }}>{selectedEntry.expert_name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{selectedEntry.project_name} · {selectedEntry.client || '—'}</div>
                    <div style={{ fontFamily: 'var(--font-data)', fontSize: 12, color: 'var(--text-3)', marginTop: 4 }}>{selectedParsed?.formatted ?? '—'}</div>
                  </div>
                  <button className="sched-xbtn" style={xBtn} onClick={() => { setSelectedChipId(null); setReschedDate(''); setReschedTime(''); }}>✕</button>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    type="date"
                    style={{ ...inputStyle, width: 'auto', flex: '0 0 auto', fontFamily: 'var(--font-data)' }}
                    value={reschedDate}
                    onChange={(e) => setReschedDate(e.target.value)}
                  />
                  <input
                    type="time"
                    style={{ ...inputStyle, width: 'auto', flex: '0 0 auto', fontFamily: 'var(--font-data)' }}
                    value={reschedTime}
                    onChange={(e) => setReschedTime(e.target.value)}
                  />
                  <button
                    className="r-btn r-btn-primary"
                    style={{ height: 28, ...(!reschedDate || !reschedTime ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
                    disabled={!reschedDate || !reschedTime}
                    onClick={async () => {
                      const ok = await patchEntry(selectedEntry.id, { scheduled_at: `${reschedDate}T${reschedTime}` });
                      if (ok) { setSelectedChipId(null); setReschedDate(''); setReschedTime(''); }
                    }}
                  >Save</button>
                  <button
                    className="r-btn r-btn-ghost"
                    style={{ height: 28, fontSize: 12 }}
                    onClick={async () => {
                      if (!window.confirm(`Unschedule ${selectedEntry.expert_name}? Returns to Pending.`)) return;
                      const ok = await patchEntry(selectedEntry.id, { scheduled_at: null });
                      if (ok) { setSelectedChipId(null); setReschedDate(''); setReschedTime(''); }
                    }}
                  >Unschedule</button>
                </div>
              </div>
            )}

            {confirmedRows.length === 0 && (
              <div style={{ ...emptyState, padding: '16px 0 32px' }}>No confirmed calls yet. Confirm a call from the Pending section.</div>
            )}

          </div>
        </div>

      </div>
    </>
  );
}
